import { Alert, AlertTitle, Box, Button, Modal, Typography, IconButton } from "@mui/material"
import { Close as CloseIcon } from '@mui/icons-material'
import { schemas } from "../generated/api/client"
import { z } from "zod"
import { apiClient, apiClientHooks } from "../bootstrapping/InitApiClient"
import { queryClient } from "../bootstrapping/InitReactQuery"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import IconButtonWithTooltip, { IconButtonWithTooltipProps } from "./IconButtonWithTooltip"

type EBulkApplication = z.infer<typeof schemas.EBulkApplication>

const AutomatedVerificationButton: React.FC<{ dbsApp: EBulkApplication } & Omit<IconButtonWithTooltipProps, 'onclick'>> = ({ dbsApp, ...iconButtonWithProps }) => {
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 550,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
  };

  const zLocalFormScheman = z.object({
    reason: z.string(),
    notify_customer: z.boolean().optional().default(true),
    notify_applicant: z.boolean().optional().default(true),
  })
  type LocalFormScheman = z.infer<typeof zLocalFormScheman>
  const form = useForm<LocalFormScheman>({
    resolver: zodResolver(zLocalFormScheman),
    defaultValues: {
      reason: '',
      notify_customer: true,
      notify_applicant: true,
    },
    mode: 'onBlur',
  })

  const customerQuery = apiClientHooks.useGetCustomerById({
    params: { id: dbsApp.customer_id ?? '' },
    enabled: !!dbsApp.customer_id,
  })

  const isApplicationEligibleForAutomatedVerification = dbsApp.status === 'awaiting_customer_approval'
  const isCustomerEligibleForAutomatedVerification = ['manual', 'auto'].includes(customerQuery.data?.automated_verification_trigger ?? '')
  
  return isApplicationEligibleForAutomatedVerification
    ? <>
      <IconButtonWithTooltip
        {...iconButtonWithProps}
        disabled={!isCustomerEligibleForAutomatedVerification}
        onClick={isCustomerEligibleForAutomatedVerification ? handleOpen : undefined}
      />
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography id="modal-modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
            Proceed with Applicant Automated Verification
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>What happens next?</AlertTitle>
            You are about to send this application to Automated Verification which the applicant can follow themselves.
            <br /><br />
            The applicant will be notified and instructed on how to proceed.
          </Alert>
          <Button
            variant="contained"
            color="success"
            onClick={() => {
              form.handleSubmit(async (data) => {
                await apiClient.manuallyTriggerAutomatedVerification(undefined, {
                  params: { id: dbsApp.id }
                })
                queryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getEBulkApplications') })
                setOpen(false)
              })()
            }}
            sx={{ mt: 2 }}
          >
            Send to Automated Verification
          </Button>
          <Button
            variant="text"
            color="primary"
            onClick={handleClose}
            sx={{ ml: 3, mt: 2 }}
          >
            Cancel
          </Button>
        </Box>
      </Modal>
    </>
    : null
}

export default AutomatedVerificationButton
